/**
 * Main Application Component
 * Integrates all components, routing, and global providers
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { CompanyProvider } from './contexts/CompanyContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LogoLoader from './components/common/LogoLoader';
import AppRouter from './routes/AppRouter';
import { initializePerformanceMonitoring } from './utils/performance';
import { initializeServiceWorker, removeOfflineIndicators } from './utils/serviceWorker';
import { config } from './config/environment';
import './App.css';

/**
 * Main App Component
 */
function App() {
  useEffect(() => {
    // Remove any existing offline indicators immediately
    removeOfflineIndicators();

    // Initialize performance monitoring
    if (config.features.enableAnalytics) {
      initializePerformanceMonitoring();
    }

    // Initialize service worker for offline support
    if (config.features.enableOfflineMode) {
      initializeServiceWorker();
    }

    // Log app initialization in development
    if (config.environment.isDevelopment) {
      // eslint-disable-next-line no-console
      console.log('🚀 Poultry Management System initialized');
      // eslint-disable-next-line no-console
      console.log('📊 Configuration:', config);
    }
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <LoadingProvider>
          <ToastProvider>
            <AuthProvider>
              <CompanyProvider>
                <LogoLoader />
                <Router>
                  <AppRouter />
                </Router>
              </CompanyProvider>
            </AuthProvider>
          </ToastProvider>
        </LoadingProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
